import { useEntryPriceAfterSpread } from '@ui/features/Trade/hooks/useEntryPriceAfterSpread';
import { useLiqPriceAfterSpread } from '@ui/features/Trade/hooks/useLiqPriceAfterSpread';
import { useOpenPositionPairSpreadPercentage } from '@ui/features/Trade/hooks/useOpenPositionPairSpreadPercentage';
import { useLeverage } from '@ui/features/Trade/store/useLeverage';
import { useTradingPair } from '@ui/features/Trade/store/useTradingPair';
import { TpSlValueState } from '@ui/features/Trade/types';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { OrderType } from '@ui/types/trade';
import { getTakeProfitStopLossPrice } from '@ui/utils/trade/getTakeProfitStopLossPrice';
import { useCallback, useMemo, useState } from 'react';
import { Box, Flex, HstackProps, styled } from 'styled-system/jsx';

import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { NoSSR } from '@ui/components/NoSSR';
import { formatNumber } from '@ui/lib/bignumber';
import { Text } from '@ui/lib/park/ui/text';
import { ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'next-i18next';
import BigNumber from 'bignumber.js';

import { useMarketPrice } from '../../hooks/useMarketPrice';
import { useStableMarketPrice } from '../../hooks/useStableMarketPrice';
import { useStopLossStore } from '../../store/useStopLossStore';
import { useTakeProfitStore } from '../../store/useTakeProfitStore';
import { getTpQuickMaxRatio } from '../../utils/getTpQuickRatios';
import { validateStopLossPrice } from '../../utils/validateStoppLossPrice';
import { validateTakeProfitPrice } from '../../utils/validateTakeProfitPrice';
import { useCurrentPairMaxTpRatios } from './hooks/useCurrentPairMaxTpRatios';
import { useEstimatedCollateralUsd } from './hooks/useEstimatedCollateralUsd';
import { useIsLong } from './store/useIsLong';
import { useOrderType } from './store/useOrderType';
import { TPSLInputSection } from './TPSLInputSection';

export const TPSLInput = (p: HstackProps) => {
  const { t } = useTranslation();
  const tpValue = useTakeProfitStore((s) => s.tpValue);
  const slValue = useStopLossStore((s) => s.slValue);
  
  const setTpState = useCallback((value: React.SetStateAction<TpSlValueState>) => {
    const currentTp = useTakeProfitStore.getState().tpValue;
    const newValue = typeof value === 'function' ? value(currentTp) : value;
    useTakeProfitStore.getState().setState(newValue);
  }, []);

  const setSlState = useCallback((value: React.SetStateAction<TpSlValueState>) => {
    const currentSl = useStopLossStore.getState().slValue;
    const newValue = typeof value === 'function' ? value(currentSl) : value;
    useStopLossStore.getState().setState(newValue);
  }, []);

  const leverage = useLeverage();
  const isLong = useIsLong((s) => s._persistValue);
  const selectedTradingPair = useTradingPair((s) => s.useTradingPair());
  const priceDisplayDecimals = useTokenDisplayDecimals(
    selectedTradingPair.baseSymbol,
    selectedTradingPair.priceDisplayDecimals,
  );
  const marketPrice = useStableMarketPrice();
  const liveMarketPrice = useMarketPrice();
  const maxTpRatios = useCurrentPairMaxTpRatios();
  
  const entryPrice = useEntryPriceAfterSpread();
  const estimatedCollateralUsd = useEstimatedCollateralUsd();
  const openPositionPairSpreadPercentage = useOpenPositionPairSpreadPercentage({
    isClosePrice: true,
  });
  const orderType = useOrderType((s) => s._persistValue);
  const isMarket = orderType === OrderType.MARKET;

  const entryOrMarketPrice = entryPrice?.toNumber() || marketPrice;
  const maxTpQuickRatio = getTpQuickMaxRatio(maxTpRatios);

  const maxTpRatiosPrice = useMemo(() => {
    if (
      entryOrMarketPrice == null ||
      leverage == null ||
      openPositionPairSpreadPercentage == null
    ) {
      return undefined;
    }

    return getTakeProfitStopLossPrice({
      pnlRate: maxTpQuickRatio,
      isTakeProfit: true,
      entryPrice: entryOrMarketPrice,
      leverage,
      isLong,
      spreadPercentage: openPositionPairSpreadPercentage,
    });
  }, [
    entryOrMarketPrice,
    isLong,
    leverage,
    maxTpQuickRatio,
    openPositionPairSpreadPercentage,
  ]);

  const takeProfitPriceValidation = validateTakeProfitPrice({
    isLong,
    entryPrice: entryPrice?.toNumber(),
    tp: tpValue,
    maxTpRatios: maxTpQuickRatio,
    maxTpRatiosPrice,
    marketPrice: liveMarketPrice,
    isMarket,
  });

  const liqPrice = useLiqPriceAfterSpread();

  const stopLossPriceValidation = validateStopLossPrice({
    isLong,
    entryPrice: entryPrice?.toNumber(),
    sl: slValue,
    liqPrice,
    marketPrice: liveMarketPrice,
    isMarket,
  });

  // V2: TP/SL ships collapsed — a 36px summary bar expands into the editor
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Box
      border="1px solid"
      borderColor="v2.border.default"
      borderRadius="2px"
      overflow="hidden"
      {...p}
    >
      <styled.button
        type="button"
        display="flex"
        w="full"
        h="36px"
        px="10px"
        alignItems="center"
        justifyContent="space-between"
        gap="8px"
        cursor="pointer"
        bg="v2.bg.surface1"
        _hover={{ bg: 'v2.bg.surface2' }}
        transition="background-color 0.15s"
        onClick={() => setIsExpanded((v) => !v)}
        aria-expanded={isExpanded}
        data-cy="TpSlSummaryBar"
      >
        <Text
          textStyle="v2.sectionLabel"
          color="v2.text.subtle"
          whiteSpace="nowrap"
        >
          {t('common.TP/SL', 'TP / SL')}
        </Text>
        <NoSSR>
          <TpSlSummaryValue tp={tpValue} sl={slValue} />
        </NoSSR>
        <Box
          color="v2.text.subtle"
          transform={isExpanded ? 'rotate(180deg)' : undefined}
          transition="transform 0.15s"
          flexShrink={0}
        >
          <ChevronDownIcon size={12} />
        </Box>
      </styled.button>
      {/* Always mounted so the default-TP initialization inside the editor
          still runs while collapsed — collapse is visual only */}
      <Box
        display={isExpanded ? 'block' : 'none'}
        px="10px"
        pb="10px"
        pt="4px"
        borderTop="1px solid"
        borderTopColor="v2.border.hairline"
      >
          <TPSLInputSection
            tp={tpValue}
            setTp={setTpState}
            sl={slValue}
            setSl={setSlState}
            entryPrice={entryOrMarketPrice || 1} // for open position, entry price === market price, for limit order, entry price === trigger price
            leverage={leverage}
            collateralUsd={estimatedCollateralUsd}
            isLong={isLong}
            priceDisplayDecimals={priceDisplayDecimals}
            maxTpRatios={maxTpRatios}
            tpValidation={takeProfitPriceValidation}
            slValidation={stopLossPriceValidation}
            spreadPercentage={openPositionPairSpreadPercentage}
            defaultTpEnabled={true}
            quickPanel
          />
      </Box>
    </Box>
  );
};

/** Collapsed-bar readout: "TP 500% · +$997.10 · SL None" */
const TpSlSummaryValue = ({
  tp,
  sl,
}: {
  tp: TpSlValueState;
  sl: TpSlValueState;
}) => {
  const { t } = useTranslation();
  const none = t('common.None', 'None');

  // pnlPercentage is stored as a multiple (5 → 500%)
  const tpLabel =
    tp?.pnlPercentage != null && tp.pnlPercentage !== 0
      ? `TP ${formatNumber(tp.pnlPercentage * 100, { displayDecimals: 0 })}%`
      : `TP ${none}`;
  const slLabel =
    sl?.pnlPercentage != null && sl.pnlPercentage !== 0
      ? `SL ${formatNumber(sl.pnlPercentage * 100, { displayDecimals: 0 })}%`
      : `SL ${none}`;
  const tpProfit =
    tp?.deltaPnl != null && tp.deltaPnl !== 0
      ? new BigNumber(tp.deltaPnl)
      : undefined;

  return (
    <Flex
      flex={1}
      minW={0}
      alignItems="center"
      justifyContent="flex-end"
      gap="4px"
      textStyle="numbers.xsmall"
      lineHeight="1.2"
      color="v2.text.secondary"
      whiteSpace="nowrap"
      overflow="hidden"
    >
      {/* lineHeight 1.2 keeps the mono readout inside the 36px bar — the
          textStyle's fixed px line-height clipped the glyph tops */}
      <Text as="span" textStyle="numbers.xsmall" lineHeight="1.2">
        {tpLabel}
      </Text>
      {tpProfit && (
        <>
          <Text as="span" color="v2.text.subtle" lineHeight="1.2">
            ·
          </Text>
          <AmountUsd
            textStyle="numbers.xsmall"
            lineHeight="1.2"
            color="v2.long.default"
            value={tpProfit}
            opts={{ isShowSign: true }}
          />
        </>
      )}
      <Text as="span" color="v2.text.subtle" lineHeight="1.2">
        ·
      </Text>
      <Text as="span" textStyle="numbers.xsmall" lineHeight="1.2">
        {slLabel}
      </Text>
    </Flex>
  );
};
